/**
 * TaxSetu AI - Privacy Policy Page
 * ==================================
 * Required for Razorpay verification
 * DPDP Act 2023 Compliant
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, CheckCircle } from 'lucide-react';

export default function PrivacyPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Header */}
      <header className="bg-[#0f2744] border-b border-[#1e3a5f] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-white">
            Tax<span className="text-[#38bdf8]">Setu</span> AI
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-8 h-8 text-[#38bdf8]" />
          <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
        </div>
        
        {/* DPDP Compliance Badge */}
        <div className="mb-8 inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
          <CheckCircle className="w-4 h-4 text-green-400" />
          <span className="text-green-400 text-sm font-medium">DPDP Act 2023 Compliant</span>
        </div>
        
        <div className="bg-[#0f2744] rounded-xl p-8 border border-[#1e3a5f] prose prose-invert max-w-none">
          <p className="text-gray-400 text-sm mb-6">Last Updated: February 3, 2026</p>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="text-gray-300 leading-relaxed">
              Credence Tax Advisors (OPC) Private Limited ("we", "us", "our") operates TaxSetu AI (https://taxsetu.ai). 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our Service, in compliance with the <strong className="text-cyan-400">Digital Personal Data Protection Act, 2023 (DPDP Act)</strong> of India.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-2">2.1 Personal Information</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              When you sign up using Google OAuth, we collect:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Name</li>
              <li>Email address</li>
              <li>Profile picture (if available)</li>
              <li>Google account ID</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">2.2 Usage Data</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              We automatically collect:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Search queries and AI interactions</li>
              <li>Device information (browser type, operating system)</li>
              <li>IP address and approximate geographic location (State/City/Country)</li>
              <li>Session duration and feature usage</li>
            </ul>

            {/* IP Geolocation Notice */}
            <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <h4 className="text-amber-400 font-medium mb-2">📍 Location Data Notice</h4>
              <p className="text-gray-300 text-sm">
                We use your IP address to determine your approximate geographic location (State, City, Country) 
                for the following purposes:
              </p>
              <ul className="list-disc list-inside text-gray-400 text-sm mt-2 space-y-1">
                <li>To provide region-specific tax information and recommendations</li>
                <li>To understand our user base across different states for service optimization</li>
                <li>To comply with jurisdictional requirements for tax-related services</li>
                <li>For analytics and service improvement</li>
              </ul>
              <p className="text-gray-400 text-sm mt-2">
                This location data is processed in accordance with the DPDP Act. You can request deletion of this data at any time.
              </p>
            </div>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">2.3 Payment Information</h3>
            <p className="text-gray-300 leading-relaxed">
              Payment processing is handled by Razorpay. We do not store your complete credit card 
              numbers or bank account details. We only receive transaction confirmation and payment status.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>To provide and maintain our Service</li>
              <li>To process transactions and send related information</li>
              <li>To improve our AI responses and user experience</li>
              <li>To send service updates and promotional communications (with your consent)</li>
              <li>To detect and prevent fraud or abuse</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">4. Data Sharing</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We do not sell your personal information. We may share data with:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Service Providers:</strong> Razorpay (payments), Google (authentication), cloud hosting providers</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">5. Data Security</h2>
            <p className="text-gray-300 leading-relaxed">
              We implement industry-standard security measures including:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 mt-4">
              <li>SSL/TLS encryption for data in transit</li>
              <li>Encrypted database storage</li>
              <li>Regular security audits</li>
              <li>Access controls and authentication</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">6. Data Retention</h2>
            <p className="text-gray-300 leading-relaxed">
              We retain your personal data for as long as your account is active or as needed to provide 
              services. Query history is retained for 12 months for improving our AI. You can request 
              deletion of your data at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">7. Your Rights under DPDP Act 2023</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Under the Digital Personal Data Protection Act, 2023, you have the following rights:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="p-4 bg-[#1a3654] rounded-lg">
                <h4 className="text-white font-medium mb-2">Right to Access</h4>
                <p className="text-gray-400 text-sm">Request a summary of your personal data being processed.</p>
              </div>
              <div className="p-4 bg-[#1a3654] rounded-lg">
                <h4 className="text-white font-medium mb-2">Right to Correction</h4>
                <p className="text-gray-400 text-sm">Request correction of inaccurate or incomplete data.</p>
              </div>
              <div className="p-4 bg-[#1a3654] rounded-lg">
                <h4 className="text-white font-medium mb-2">Right to Erasure</h4>
                <p className="text-gray-400 text-sm">Request deletion of your data, subject to legal requirements.</p>
              </div>
              <div className="p-4 bg-[#1a3654] rounded-lg">
                <h4 className="text-white font-medium mb-2">Right to Grievance Redressal</h4>
                <p className="text-gray-400 text-sm">Lodge complaints with our Data Protection Officer.</p>
              </div>
            </div>
            <p className="text-gray-300 mt-4">
              To exercise any of these rights, contact us at <span className="text-cyan-400">privacy@taxsetu.ai</span>. 
              We will respond within 7 working days as required by the DPDP Act.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">8. Cookies</h2>
            <p className="text-gray-300 leading-relaxed">
              We use essential cookies for authentication and session management. We also use 
              analytics cookies (Google Analytics) to understand how users interact with our Service. 
              You can control cookie preferences through your browser settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">9. Children's Privacy</h2>
            <p className="text-gray-300 leading-relaxed">
              Our Service is not intended for users under 18 years of age. We do not knowingly 
              collect information from children under 18.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new policy on this page and updating the "Last Updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">11. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed">
              For privacy-related inquiries, please contact:
            </p>
            <div className="mt-4 p-4 bg-[#1a3654] rounded-lg">
              <p className="text-white font-medium">Credence Tax Advisors (OPC) Pvt. Ltd.</p>
              <p className="text-gray-400">Email: support@taxsetu.ai</p>
              <p className="text-gray-400">Phone: +91 9711162342</p>
              <p className="text-gray-400">Address: A-50, Street-3, New Modern Shahdara, Delhi-110032</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
